import { NormalizeDepCdKey } from '../Step01_SafetyInfo/SafetyInfo_Schema';

/** 문자열 정규화 (공백 trim) */
export const Norm = (s) => String(s ?? '').trim();

/** 엑셀 파싱 행을 공정명 기준으로 그룹화 */
export const GroupExcelRowsByProcessNm = (parsed) => {
  const groups = new Map();
  for (const row of Array.isArray(parsed) ? parsed : []) {
    const processNm = Norm(row?.gongJongNm);
    if (!processNm) continue;
    if (!groups.has(processNm)) groups.set(processNm, []);
    groups.get(processNm).push(row);
  }
  return groups;
};

/**
 * 평가 공정 목록에서 공정명 우선 매칭 (부서 불일치로 제외하지 않음)
 * @returns {{ candidates: object[], autoMatch: object|null, ambiguous: boolean }}
 */
export const MatchProcessCandidates = (processNm, depNm, processes) => {
  const pn = Norm(processNm);
  const dn = Norm(depNm);
  const candidates = (Array.isArray(processes) ? processes : []).filter(
    (p) => Norm(p?.processNm) === pn
  );

  if (candidates.length === 0) {
    return { candidates: [], autoMatch: null, ambiguous: false };
  }
  if (candidates.length === 1) {
    return { candidates, autoMatch: candidates[0], ambiguous: false };
  }

  if (dn) {
    const depMatches = candidates.filter((p) => Norm(p?.depNm) === dn);
    if (depMatches.length === 1) {
      return { candidates: depMatches, autoMatch: depMatches[0], ambiguous: false };
    }
    if (depMatches.length > 1) {
      return { candidates: depMatches, autoMatch: null, ambiguous: true };
    }
  }

  return { candidates, autoMatch: null, ambiguous: true };
};

/**
 * TB_BS_GONGJONG 마스터(GetRisCustom01R2 gongjongList)에서 dep+name 해석
 * @returns {{ match: object|null, candidates: object[], ambiguous: boolean, needsDept: boolean }}
 */
export const ResolveMasterGongjong = (processNm, depNm, gongjongList) => {
  const pn = Norm(processNm);
  const dn = Norm(depNm);
  const list = (Array.isArray(gongjongList) ? gongjongList : []).filter(
    (g) => Norm(g?.gongJongNm) === pn
  );

  if (list.length === 0) {
    return { match: null, candidates: [], ambiguous: false, needsDept: false };
  }
  if (list.length === 1) {
    return { match: list[0], candidates: list, ambiguous: false, needsDept: false };
  }

  if (dn) {
    const depMatches = list.filter((g) => Norm(g?.depNm) === dn);
    if (depMatches.length === 1) {
      return {
        match: depMatches[0],
        candidates: depMatches,
        ambiguous: false,
        needsDept: false,
      };
    }
    if (depMatches.length > 1) {
      return {
        match: null,
        candidates: depMatches,
        ambiguous: true,
        needsDept: false,
      };
    }
  }

  const uniqueDepNms = [...new Set(list.map((g) => Norm(g?.depNm)).filter(Boolean))];
  if (uniqueDepNms.length > 1 && !dn) {
    return { match: null, candidates: list, ambiguous: true, needsDept: true };
  }

  return { match: null, candidates: list, ambiguous: true, needsDept: false };
};

/** depData에서 부서명으로 depCd 조회 */
export const FindDepCdByNm = (depNm, depData) => {
  const dn = Norm(depNm);
  if (!dn) return '';
  const found = (Array.isArray(depData) ? depData : []).find(
    (d) => Norm(d?.label) === dn
  );
  return found ? NormalizeDepCdKey(found.value) : '';
};

/** depData에서 depCd로 부서명 조회 */
export const FindDepNmByCd = (depCd, depData) => {
  const cd = NormalizeDepCdKey(depCd);
  if (!cd) return '';
  const found = (Array.isArray(depData) ? depData : []).find(
    (d) => NormalizeDepCdKey(d?.value) === cd
  );
  return found ? Norm(found.label) : '';
};

/** SaveRisCustom01U1 응답에서 GongJongCd 추출 (배치/중첩 응답 대응) */
export const ExtractGongJongCdFromSaveU1Response = (result) => {
  const Visit = (node, depth = 0) => {
    if (node == null || depth > 6) return '';
    if (typeof node === 'string' || typeof node === 'number') return '';
    if (Array.isArray(node)) {
      for (const item of node) {
        const found = Visit(item, depth + 1);
        if (found) return found;
      }
      return '';
    }
    if (typeof node === 'object') {
      const direct = Norm(node.gongJongCd ?? node.GongJongCd);
      if (direct) return direct;
      const nested = node.data ?? node.Data ?? node.result ?? node.Result;
      return Visit(nested, depth + 1);
    }
    return '';
  };
  return Visit(result);
};

/** 모호한 공정 그룹 목록 수집 */
export const CollectAmbiguousExcelGroups = (
  excelGroups,
  processes,
  gongjongList
) => {
  const ambiguous = [];
  for (const [processNm, rows] of excelGroups) {
    const excelDepNm = Norm(rows?.[0]?.depNm);
    const assessment = MatchProcessCandidates(processNm, excelDepNm, processes);
    if (assessment.ambiguous && assessment.candidates.length > 1) {
      ambiguous.push({
        processNm,
        excelDepNm,
        kind: 'assessment',
        rowCount: rows.length,
        candidates: assessment.candidates.map((p) => ({
          kind: 'assessment',
          key: `a-${p.sriId ?? 0}-${Norm(p.depNm)}`,
          label: `${Norm(p.depNm) || '(부서 없음)'} · ${Norm(p.processNm)}`,
          sriId: p.sriId,
          depNm: p.depNm,
          depCd: p.depCd,
          processNm: p.processNm,
          gongJongCd: p.gongJongCd ?? p.processId,
          data: p,
        })),
      });
      continue;
    }
    if (assessment.autoMatch) continue;

    const master = ResolveMasterGongjong(processNm, excelDepNm, gongjongList);
    if (master.ambiguous && master.candidates.length > 1) {
      ambiguous.push({
        processNm,
        excelDepNm,
        kind: 'master',
        rowCount: rows.length,
        candidates: master.candidates.map((g) => ({
          kind: 'master',
          key: `m-${NormalizeDepCdKey(g.depCd)}-${Norm(g.gongJongCd)}`,
          label: `${Norm(g.depNm) || '(부서 없음)'} · ${Norm(g.gongJongNm)}`,
          depCd: g.depCd,
          depNm: g.depNm,
          gongJongCd: g.gongJongCd,
          gongJongNm: g.gongJongNm,
          data: g,
        })),
      });
    }
  }
  return ambiguous;
};

/** U2 저장용 공정 1건 */
export const BuildU2ProcessItem = (resolved, existingProcess, index) => {
  const ep = existingProcess && typeof existingProcess === 'object' ? existingProcess : {};
  return {
    ...ep,
    sriId: Number(ep?.sriId) > 0 ? Number(ep.sriId) : 0,
    processId: resolved.gongJongCd,
    processNm: resolved.processNm,
    depCd: resolved.depCd,
    depNm: resolved.depNm,
    processNo: index + 1,
    selected: index === 0,
    equipmentList: Array.isArray(ep?.equipmentList) ? ep.equipmentList : [],
    materialList: Array.isArray(ep?.materialList) ? ep.materialList : [],
  };
};
